import React, { memo, useState } from 'react';
import { Popover, Button, Switch, Tooltip } from 'antd';
import {
  SettingOutlined,
  HolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  HiOutlineChartBar,
  HiOutlineUsers,
  HiOutlineClipboardList,
  HiOutlineTrendingUp,
  HiOutlineChartPie,
  HiOutlineClock,
} from 'react-icons/hi';
import {
  PopoverContainer,
  PopoverHeader,
  HeaderTitleGroup,
  HeaderTitle,
  CountBadge,
  HeaderActions,
  ActionLinkButton,
  SubHeaderHint,
  WidgetList,
  WidgetRowItem,
  DragAndOrderControls,
  DragHandle,
  OrderArrowGroup,
  OrderArrowBtn,
  TypeIconBadge,
  WidgetInfo,
  WidgetTitleRow,
  WidgetTitle,
  TypeLabelTag,
  WidgetDescription,
  SwitchWrapper,
} from './DashboardWidgetSettingsPopover.styles';

export type DashboardWidgetKey =
  | 'overview'
  | 'formsProgress'
  | 'staffPerformance'
  | 'dailyTrend'
  | 'staffShare'
  | 'activityBreakdown';

export interface DashboardWidgetConfigItem {
  key: DashboardWidgetKey;
  label: string;
  description: string;
  tag: string;
  tagColor: string;
  color: string;
  visible: boolean;
}

export const DEFAULT_DASHBOARD_WIDGETS: DashboardWidgetConfigItem[] = [
  {
    key: 'overview',
    label: 'Tổng quan',
    description: 'Tổng số bản ghi thu thập, tỷ lệ hoàn thành và tỷ lệ lỗi',
    tag: 'KPI',
    tagColor: 'blue',
    color: '#2563eb',
    visible: true,
  },
  {
    key: 'formsProgress',
    label: 'Tiến độ theo Biểu mẫu',
    description: 'Tiến độ thu thập và chỉ tiêu KPI theo từng biểu mẫu',
    tag: 'Biểu mẫu',
    tagColor: 'green',
    color: '#16a34a',
    visible: true,
  },
  {
    key: 'staffPerformance',
    label: 'Top Nhân viên',
    description: 'Bảng xếp hạng nhân viên theo số lượng bản ghi thu thập',
    tag: 'Nhân sự',
    tagColor: 'purple',
    color: '#7c3aed',
    visible: true,
  },
  {
    key: 'dailyTrend',
    label: 'Xu hướng thu thập theo ngày',
    description: 'Biểu đồ sản lượng thu thập qua từng ngày và ngày cao điểm',
    tag: 'Chart',
    tagColor: 'cyan',
    color: '#0284c7',
    visible: true,
  },
  {
    key: 'staffShare',
    label: 'Tỷ trọng đóng góp Nhân viên',
    description: 'Biểu đồ Donut tỷ lệ phần trăm (%) đóng góp theo nhân viên',
    tag: 'Chart',
    tagColor: 'geekblue',
    color: '#0d9488',
    visible: true,
  },
  {
    key: 'activityBreakdown',
    label: 'Phân bố Hình học & Khung giờ',
    description: 'Cơ cấu Điểm/Đường/Vùng và mật độ thu thập theo khung giờ',
    tag: 'Chart',
    tagColor: 'orange',
    color: '#d97706',
    visible: true,
  },
];

const renderWidgetIcon = (key: DashboardWidgetKey, color: string) => {
  switch (key) {
    case 'overview':
      return <HiOutlineChartBar style={{ color, fontSize: 15 }} />;
    case 'formsProgress':
      return <HiOutlineClipboardList style={{ color, fontSize: 15 }} />;
    case 'staffPerformance':
      return <HiOutlineUsers style={{ color, fontSize: 15 }} />;
    case 'dailyTrend':
      return <HiOutlineTrendingUp style={{ color, fontSize: 15 }} />;
    case 'staffShare':
      return <HiOutlineChartPie style={{ color, fontSize: 15 }} />;
    case 'activityBreakdown':
      return <HiOutlineClock style={{ color, fontSize: 15 }} />;
    default:
      return <HiOutlineChartBar style={{ color, fontSize: 15 }} />;
  }
};

interface SortableWidgetRowProps {
  item: DashboardWidgetConfigItem;
  index: number;
  total: number;
  onToggle: (key: DashboardWidgetKey, checked: boolean) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
}

const SortableWidgetRow = memo(({
  item,
  index,
  total,
  onToggle,
  onMove,
}: SortableWidgetRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key });

  const { role: _ignoredRole, ...cleanAttributes } = attributes;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.88 : 1,
  };

  return (
    <WidgetRowItem
      ref={setNodeRef}
      style={style}
      $visible={item.visible}
      $isDragging={isDragging}
      data-testid={`widget-setting-row-${item.key}`}
    >
      <DragAndOrderControls>
        <DragHandle
          {...cleanAttributes}
          {...listeners}
          $isDragging={isDragging}
          aria-label={`Kéo thả hoặc dùng phím mũi tên để sắp xếp widget ${item.label}`}
          title="Kéo thả để sắp xếp vị trí widget"
          data-testid={`widget-setting-drag-${item.key}`}
        >
          <HolderOutlined />
        </DragHandle>
        <OrderArrowGroup>
          <OrderArrowBtn
            type="button"
            disabled={index === 0}
            title="Di chuyển lên"
            data-testid={`widget-move-up-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              if (index > 0) onMove(index, index - 1);
            }}
          >
            <ArrowUpOutlined />
          </OrderArrowBtn>
          <OrderArrowBtn
            type="button"
            disabled={index === total - 1}
            title="Di chuyển xuống"
            data-testid={`widget-move-down-${item.key}`}
            onClick={(e) => {
              e.stopPropagation();
              if (index < total - 1) onMove(index, index + 1);
            }}
          >
            <ArrowDownOutlined />
          </OrderArrowBtn>
        </OrderArrowGroup>
      </DragAndOrderControls>

      <TypeIconBadge $color={item.color} title={item.label}>
        {renderWidgetIcon(item.key, item.color)}
      </TypeIconBadge>

      <WidgetInfo>
        <WidgetTitleRow>
          <WidgetTitle $visible={item.visible} title={item.label}>
            {item.label}
          </WidgetTitle>
          <TypeLabelTag color={item.tagColor}>
            {item.tag}
          </TypeLabelTag>
        </WidgetTitleRow>
        <WidgetDescription title={item.description}>
          {item.description}
        </WidgetDescription>
      </WidgetInfo>

      <SwitchWrapper onClick={(e) => e.stopPropagation()}>
        <Switch
          size="small"
          checked={item.visible}
          data-testid={`widget-switch-${item.key}`}
          onChange={(checked) => onToggle(item.key, checked)}
        />
      </SwitchWrapper>
    </WidgetRowItem>
  );
});

export interface DashboardWidgetSettingsPopoverProps {
  widgets: DashboardWidgetConfigItem[];
  onChangeWidgets: (nextWidgets: DashboardWidgetConfigItem[]) => void;
  onResetWidgets: () => void;
}

const DashboardWidgetSettingsPopover = ({
  widgets,
  onChangeWidgets,
  onResetWidgets,
}: DashboardWidgetSettingsPopoverProps) => {
  const [open, setOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const visibleCount = widgets.filter((w) => w.visible).length;
  const allVisible = visibleCount === widgets.length;
  const isDefaultOrder = widgets.every(
    (w, idx) => w.key === DEFAULT_DASHBOARD_WIDGETS[idx]?.key,
  );
  const isCustomized = !allVisible || !isDefaultOrder;

  const handleToggleWidget = (key: DashboardWidgetKey, checked: boolean) => {
    const next = widgets.map((w) =>
      w.key === key ? { ...w, visible: checked } : w,
    );
    onChangeWidgets(next);
  };

  const handleMoveWidget = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= widgets.length ||
      toIndex >= widgets.length
    ) {
      return;
    }
    onChangeWidgets(arrayMove(widgets, fromIndex, toIndex));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = widgets.findIndex((w) => w.key === active.id);
      const newIndex = widgets.findIndex((w) => w.key === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChangeWidgets(arrayMove(widgets, oldIndex, newIndex));
      }
    }
  };

  const handleShowAll = () => {
    onChangeWidgets(widgets.map((w) => ({ ...w, visible: true })));
  };

  const popoverContent = (
    <PopoverContainer data-testid="dashboard-widget-settings-popover">
      <PopoverHeader>
        <HeaderTitleGroup>
          <SettingOutlined style={{ color: '#2563eb', fontSize: 14 }} />
          <HeaderTitle>Cấu hình Widget hiển thị</HeaderTitle>
          <CountBadge>
            {visibleCount}/{widgets.length}
          </CountBadge>
        </HeaderTitleGroup>
        <HeaderActions>
          {!allVisible && (
            <ActionLinkButton
              type="button"
              onClick={handleShowAll}
              title="Hiển thị tất cả các widget"
              data-testid="btn-show-all-dashboard-widgets"
            >
              <CheckCircleOutlined /> Bật tất cả
            </ActionLinkButton>
          )}
          <ActionLinkButton
            type="button"
            onClick={onResetWidgets}
            title="Khôi phục thứ tự và trạng thái hiển thị mặc định"
            data-testid="btn-reset-dashboard-widgets"
          >
            <ReloadOutlined /> Đặt lại
          </ActionLinkButton>
        </HeaderActions>
      </PopoverHeader>

      <SubHeaderHint>
        Kéo thả hoặc nhấn mũi tên để sắp xếp vị trí widget, bật/tắt công tắc để ẩn/hiện widget.
      </SubHeaderHint>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={widgets.map((w) => w.key)}
          strategy={verticalListSortingStrategy}
        >
          <WidgetList role="list">
            {widgets.map((widget, idx) => (
              <SortableWidgetRow
                key={widget.key}
                item={widget}
                index={idx}
                total={widgets.length}
                onToggle={handleToggleWidget}
                onMove={handleMoveWidget}
              />
            ))}
          </WidgetList>
        </SortableContext>
      </DndContext>
    </PopoverContainer>
  );

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow:
          '0 10px 25px -5px rgba(15, 23, 42, 0.16), 0 8px 10px -6px rgba(15, 23, 42, 0.08)',
        border: '1px solid #e2e8f0',
      }}
    >
      <Tooltip title="Cấu hình Widget hiển thị" open={open ? false : undefined}>
        <Button
          type="text"
          size="small"
          icon={<SettingOutlined />}
          data-testid="btn-dashboard-widget-settings"
          style={
            open || isCustomized
              ? { color: '#2563eb', background: '#eff6ff' }
              : undefined
          }
        />
      </Tooltip>
    </Popover>
  );
};

export default memo(DashboardWidgetSettingsPopover);

